/**
 * React Hooks for Deal Operations
 * Includes hooks for the TRIAGE business logic
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Deal,
  DealStage,
  DealCategory,
  DealComplexity,
  StarlinkKitOption,
} from '../types/theo-workflow.types';
import * as dealService from '../services/deal.service';
import useTenantId, { tenantIdReadUnion } from './useTenantId';
import { createdAtMs } from '@/utils/sortHelpers';

/**
 * Hook to get all deals with real-time updates
 */
export function useDeals() {
  const tenantId = useTenantId();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // tenantId filter required by Phase 0.5 Session A.5 strict rules.
    // Firestore's "rules are not filters" semantics: the listen query
    // must constrain tenantId or the listen is rejected wholesale.
    // NOTE: no `orderBy('createdAt')` — it silently drops deals missing the
    // field (webhook/BP-created deals sometimes lack a top-level createdAt),
    // which made them invisible to global search. Sort client-side instead.
    const q = query(
      collection(db, 'deals'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId))
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const dealsData = snapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as Deal
          )
          .filter((d: Deal) => !d.deleted)
          .sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
        setDeals(dealsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching deals:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  return { deals, loading, error };
}

/**
 * Hook to get a single deal by ID with real-time updates
 */
export function useDeal(dealId: string | null) {
  const [deal, setDeal] = useState<Deal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!dealId) {
      setDeal(null);
      setLoading(false);
      return;
    }

    dealService
      .getDeal(dealId)
      .then((data) => {
        // Hide soft-deleted deals from detail views
        setDeal(data && !data.deleted ? data : null);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching deal:', err);
        setError(err);
        setLoading(false);
      });
  }, [dealId]);

  return { deal, loading, error };
}

/**
 * Hook to get deals by customer with real-time updates
 */
export function useDealsByCustomer(customerId: string | null) {
  const tenantId = useTenantId();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!customerId) {
      setDeals([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'deals'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const dealsData = snapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as Deal
          )
          .filter((d: Deal) => !d.deleted);
        setDeals(dealsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching deals by customer:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [customerId, tenantId]);

  return { deals, loading, error };
}

/**
 * Hook to get deals by stage with real-time updates
 */
export function useDealsByStage(stage: DealStage) {
  const tenantId = useTenantId();
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, 'deals'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('stage', '==', stage),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const dealsData = snapshot.docs
          .map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as Deal
          )
          .filter((d: Deal) => !d.deleted);
        setDeals(dealsData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching deals by stage:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [stage, tenantId]);

  return { deals, loading, error };
}

/**
 * Hook to create, update, and delete deals
 */
export function useDealMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createDeal = async (
    data: Omit<Deal, 'id' | 'createdAt' | 'updatedAt' | 'stage'>,
    userId?: string,
    initialStage?: DealStage
  ) => {
    setLoading(true);
    setError(null);
    try {
      const id = await dealService.createDeal(data, userId, initialStage);
      setLoading(false);
      return id;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateDeal = async (
    dealId: string,
    data: Partial<Omit<Deal, 'id' | 'createdAt' | 'createdBy'>>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.updateDeal(dealId, data);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateDealWithLog = async (
    dealId: string,
    data: Partial<Omit<Deal, 'id' | 'createdAt' | 'createdBy'>>,
    userId: string,
    userName: string,
    action: string,
    details?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.updateDealWithLog(dealId, data, userId, userName, action, details);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const deleteDeal = async (dealId: string) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.deleteDeal(dealId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const moveDealToStage = async (dealId: string, newStage: DealStage) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.moveDealToStage(dealId, newStage);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return {
    createDeal,
    updateDeal,
    updateDealWithLog,
    deleteDeal,
    moveDealToStage,
    loading,
    error,
  };
}

/**
 * 🎯 Hook for TRIAGE business logic
 *
 * Provides the triageDeal function that implements The Fork:
 * - STANDARD → QUOTING
 * - COMPLEX → SCOPING_REQUIRED
 */
export function useTriageDeal() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const triageDeal = async (
    dealId: string,
    category: DealCategory,
    complexity: DealComplexity,
    starlinkKit?: StarlinkKitOption
  ) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.triageDeal(dealId, category, complexity, starlinkKit);
      setLoading(false);
      console.log(`✅ Deal ${dealId} triaged successfully`);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return { triageDeal, loading, error };
}

/**
 * Hook for deal stage transitions
 */
export function useDealStageTransitions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const completeScopingAndMoveToQuoting = async (dealId: string) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.completeScopingAndMoveToQuoting(dealId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const markDealAsWon = async (dealId: string) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.markDealAsWon(dealId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const markDealAsLost = async (dealId: string) => {
    setLoading(true);
    setError(null);
    try {
      await dealService.markDealAsLost(dealId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return {
    completeScopingAndMoveToQuoting,
    markDealAsWon,
    markDealAsLost,
    loading,
    error,
  };
}
